import { HttpErrorResponse } from '@angular/common/http';
import { AbstractControl, UntypedFormArray, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { GlobalActions } from './global.actions';
import { isServerValidationError } from './utilities';

interface ServerValidationError {
    propertyName: string;
    errorMessage: string;
}
export class TypedFormGroup<T> extends UntypedFormGroup {
    /**
     *  Признак, указывающий что пользователь делал поптыку отправить данную форму
     */
    public readonly submitAttemptWasMade: Observable<boolean> = new BehaviorSubject<boolean>(false);
    public readonly value: T;
    public valueChanges: Observable<T>;

    public addValidators(newValidator: ValidatorFn[]): void {
        const validators = this.validator ? [...newValidator, this.validator] : newValidator
        super.setValidators(validators);
    }
    /**
     *  Если формы pristine, то прогоняет updateEntireFormValueAndValidity после чего публикует значение в submitAttemptWasMade
     */
    public markAsAttemptedToSubmit(): void {
        clearAllServerErrors(this);
        this.updateValueAndValidity();
        this.logFormErrors();
        (<BehaviorSubject<boolean>>this.submitAttemptWasMade).next(true);
    }
    public resetSubmitState(): void {
        (<BehaviorSubject<boolean>>this.submitAttemptWasMade).next(false);
        if (this.root && this.root instanceof TypedFormGroup && this.root !== this) {
            this.root.resetSubmitState();
        }
    }
    public reset(
        value?: T | null,
        options?: {
            onlySelf?: boolean;
            emitEvent?: boolean;
        }
    ): void {
        this.resetSubmitState();
        super.reset(value, options);
    }
    public getRawValue(): T {
        return super.getRawValue();
    }
    public disable(opts?: { onlySelf?: boolean; emitEvent?: boolean }): void {
        super.disable(opts);
        if (!this.disabled) {
            /* eslint-disable-next-line no-console */
            console.error('Form wasn\'t disabled');
            /* eslint-disable-next-line no-console */
            console.error(getAllEnabledControls(this));
        }
    }
    public applyServerErrorsOrThrow(error: HttpErrorResponse): void {
        if (!isServerValidationError(error)) {
            throw error;
        }
        const serverErrors = error.error as ServerValidationError[];

        serverErrors.forEach(validationError => {
            const propertyName = validationError.propertyName;
            const allErrors = serverErrors.filter(serverError => serverError.propertyName === propertyName).map(serverError => serverError.errorMessage);
            const formControl = findControlByFieldName(this, propertyName);
            appendErrors(formControl, allErrors);
        });
        this.logFormErrors();
    }

    public setValue(
        value: T,
        options?: {
            onlySelf?: boolean;
            emitEvent?: boolean;
        }
    ): void {
        super.setValue(value, options);
    }

    private logFormErrors(): void {
        if (!ETR.isProduction) {
            const errors = getAllErrors(this);
            if (Object.keys(errors || {}).length > 0) {
                /* eslint-disable-next-line no-console */
                console.warn(errors);
            }
        }
    }
}

function getAllErrors(
    /* eslint-disable-next-line @typescript-eslint/ban-types, , , , */
    control: AbstractControl
): any {
    let resultedErrors: any = control.errors ? { errors: control.errors } : null;
    if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
        for (const ck of Object.keys(control.controls)) {
            const childErrors = getAllErrors(control.controls[ck]);
            if (childErrors) {
                resultedErrors = resultedErrors ? resultedErrors : {};
                resultedErrors[ck] = childErrors;
            }
        }
    }
    return resultedErrors;
}
function getAllEnabledControls(
    /* eslint-disable-next-line @typescript-eslint/ban-types */
    control: AbstractControl
): any {
    let resultedControls: any = control.enabled ? { enabled: control.enabled } : null;
    if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
        for (const ck of Object.keys(control.controls)) {
            const enabledControls = getAllEnabledControls(control.controls[ck]);
            if (enabledControls) {
                resultedControls = resultedControls ? resultedControls : {};
                resultedControls[ck] = enabledControls;
            }
        }
    }
    return resultedControls;
}

function findControlByFieldName(form: TypedFormGroup<any>, fieldName: string): AbstractControl {
    if (fieldName === '') {
        return form;
    }
    const normalizedFieldName = getCamelCasedName(fieldName);
    let controlPath = normalizedFieldName.replace(/(\[|\]\.|\])/g, '.');
    controlPath = removeLastSymbolIfItsDot(controlPath);
    let control = form.get(controlPath);
    while (!control) {
        controlPath = removeLastSegment(controlPath);
        if (controlPath === '') {
            break;
        }
        control = form.get(controlPath);
    }

    // Если контрол disabled, то валидация на нем не нарисуется. Перекидываем сообщения на уровень выше
    if (control && control.disabled) {
        while (control.disabled && control !== control.root) {
            control = control.parent;
        }
    }

    if (!control) {
        if (!ETR.isProduction) {
            GlobalActions.displayError(`Не найдено поле ${fieldName} на форме при сопоставлении с серверной валидацией`);
        }
    }
    return control || form;
}

function removeLastSymbolIfItsDot(controlPath: string): string {
    let result = controlPath;
    if (result[result.length - 1] === '.') {
        result = result.slice(0, -1);
    }
    return result;
}

function removeLastSegment(controlPath: string): string {
    return controlPath.substring(0, controlPath.lastIndexOf('.'));
}

export function getCamelCasedName(fieldName: string): string {
    const camelCasePropertiesNames: string[] = [];

    function getCamelCaseProperty(controlName: string): string {
        let newControlName = '';
        let word = controlName;
        while (word.length > 0) {
            const camelCaseInterval = word.match(/([a-z][A-Z])|([A-Z][a-z])/g) || [];
            if (camelCaseInterval.length > 0) {
                const stringInterval = word.substring(0, word.indexOf(camelCaseInterval[0]));
                if (stringInterval.length > 0) {
                    newControlName +=
                        newControlName.length === 0
                            ? stringInterval.toLocaleLowerCase()
                            : `${stringInterval[0]}${stringInterval.substring(1, stringInterval.length).toLocaleLowerCase()}`;
                    newControlName += word[stringInterval.length];
                    word = word
                        .split('')
                        .slice(stringInterval.length + 1)
                        .join('');
                } else {
                    newControlName += newControlName.length === 0 ? word[0].toLocaleLowerCase() : word[0];
                    word = word
                        .split('')
                        .slice(1)
                        .join('');
                }
            } else {
                break;
            }
        }

        newControlName += newControlName.length === 0 ? word.toLocaleLowerCase() : `${word[0]}${word.substring(1, word.length).toLocaleLowerCase()}`;
        return newControlName;
    }

    fieldName.split('.').forEach(value => {
        camelCasePropertiesNames.push(getCamelCaseProperty(value));
    });

    return camelCasePropertiesNames.join('.');
}

function appendErrors(formControl: AbstractControl, errors: string[]): void {
    if (!!formControl.errors && !!formControl.errors.serverErrors) {
        let allErrors = formControl.errors.serverErrors;
        for (const error of errors) {
            if (allErrors.indexOf(error) === -1) {
                allErrors = allErrors.concat([error]);
            }
        }
        formControl.setErrors({ serverErrors: allErrors });
    } else {
        formControl.setErrors({ serverErrors: errors });
    }
}

function clearAllServerErrors(control: AbstractControl): void {
    if (!!control.errors) {
        const { serverErrors, ...errors }: any = control.errors;
        control.setErrors(Object.keys(errors).length === 0 ? undefined : errors);
    }

    if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
        for (const ck of Object.keys(control.controls)) {
            clearAllServerErrors(control.controls[ck]);
        }
    }
}
